import { z } from 'zod';

import { FIREWALL_FAMILIES, PROGRAM_FAMILIES, RESOLUTION_STATUSES } from './context';

export const EXPERIENCE_STATES = [
  'loading',
  'ready',
  'empty',
  'denied',
  'unavailable',
  'stale',
  'error',
  'action_pending',
  'action_committed',
  'action_failed',
  'superseded',
] as const;
export type ExperienceState = (typeof EXPERIENCE_STATES)[number];

export const PAGE_ARCHETYPES = [
  'auth_form',
  'landing_dashboard',
  'register_list',
  'work_queue',
  'detail_record',
  'workstation',
  'create_edit_form',
  'wizard',
  'settings',
  'report_export',
  'evidence_timeline',
  'public_transaction',
  'launch_gate',
] as const;
export type PageArchetype = (typeof PAGE_ARCHETYPES)[number];

export const PROVIDER_COMPLETENESS_CASES = [
  'NO_QUALIFIED_ACTOR',
  'MEANS_NOT_DISCLOSED',
  'CORRECTION_SUPERSESSION',
  'TRANSITION',
  'DEATH_OPEN_MATTER',
  'INTEGRATION_UNAVAILABLE',
] as const;
export type ProviderCompletenessCase = (typeof PROVIDER_COMPLETENESS_CASES)[number];

export const PRODUCTION_SURFACE_CONTEXTS = ['SUPPORTED', 'PILOT', 'BLOCKED', 'INTERNAL'] as const;
export const SURFACES = ['web', 'portal', 'mobile', 'document', 'export', 'email'] as const;
export const ALIAS_CLASSES = [
  'PERMANENT_COMPATIBILITY',
  'PROGRAM_SCOPED_COMPATIBILITY',
  'MIGRATION_REDIRECT',
  'INTERNAL_REEXPORT',
  'INVALID',
] as const;

export const ExperienceDestinationSchema = z.object({
  id: z.string().min(3),
  surface: z.enum(SURFACES),
  canonicalRoute: z.string().min(1),
  aliases: z
    .array(
      z.object({
        route: z.string().min(1),
        class: z.enum(ALIAS_CLASSES),
        firewallFamilies: z.array(z.enum(FIREWALL_FAMILIES)).min(1),
      }),
    )
    .default([]),
  shell: z.string().min(1),
  ownerDomain: z.string().min(1),
  ownerLane: z.string().min(1),
  pageArchetype: z.enum(PAGE_ARCHETYPES),
  firewallFamilies: z.array(z.enum(FIREWALL_FAMILIES)).min(1),
  programFamilies: z.array(z.enum(PROGRAM_FAMILIES)),
  resolutionStates: z.array(z.enum(RESOLUTION_STATUSES)).min(1),
  roles: z.array(z.string().min(1)),
  modules: z.array(z.string().min(1)),
  productionSurfaceContext: z.enum(PRODUCTION_SURFACE_CONTEXTS),
  states: z.array(z.enum(EXPERIENCE_STATES)).min(1),
  contentProfile: z.string().min(1),
  sharedSemanticProofId: z
    .string()
    .regex(/^EXP-SHARED-[A-Z0-9-]+$/)
    .nullable(),
  emptyProviderSourceFile: z.string().min(1).optional(),
  emptyProviderSemanticProofId: z
    .string()
    .regex(/^EXP-SHARED-[A-Z0-9-]+$/)
    .optional(),
  fallback: z.string().min(1),
  requiredSelectedContext: z.array(z.string().min(1)).default([]),
  cacheKeyDimensions: z.array(z.string().min(1)).default([]),
  invalidationEvents: z.array(z.string().min(1)).default([]),
  uatScenarios: z.array(z.string().min(1)).default([]),
  sourceFile: z.string().min(1),
  sourceRevision: z.string().min(7),
  classification: z.enum(['EXPLICIT', 'CONSERVATIVE_INFERRED', 'REVIEW_REQUIRED']),
});

export type ExperienceDestination = z.infer<typeof ExperienceDestinationSchema>;

export const ExperienceCapabilitySchema = z.object({
  id: z.string().regex(/^EXP-CAP-[A-Z0-9][A-Z0-9-]*$/),
  job: z.string().min(1),
  ownerDomain: z.string().min(1),
  ownerLane: z.string().min(1),
  firewallFamilies: z.array(z.enum(FIREWALL_FAMILIES)).min(1),
  programFamilies: z.array(z.enum(PROGRAM_FAMILIES)),
  roles: z.array(z.string().min(1)),
  modules: z.array(z.string().min(1)),
  entityScope: z.enum(['TENANT', 'CLIENT', 'SERVICE', 'SHIFT', 'WORKFLOW', 'SELF']),
  destinationIds: z.array(z.string().min(3)).min(1),
  states: z.array(z.enum(EXPERIENCE_STATES)).min(1),
  actionAuthority: z.array(z.string().min(1)),
  dataContract: z.string().min(1),
  vocabularyProfile: z.string().min(1),
  fallback: z.string().min(1),
  telemetryClass: z.string().min(1),
  uatScenarios: z.array(z.string().min(1)),
  sharedSemanticProofIds: z.array(z.string().regex(/^EXP-SHARED-[A-Z0-9-]+$/)),
  providerCompleteness: z
    .array(
      z.object({
        case: z.enum(PROVIDER_COMPLETENESS_CASES),
        applicability: z.enum(['REQUIRED', 'NOT_APPLICABLE']),
        reason: z.string().min(1),
        reviewedDestinationIds: z.array(z.string().min(3)).min(1),
        requiredDestinationIds: z.array(z.string().min(3)),
        requiredProgramContexts: z.array(z.enum(['SAH', 'RAC', 'OTHER', 'SHARED'])),
        scenarioId: z
          .string()
          .regex(/^EXP-CAP-[A-Z0-9-]+-DEGRADED-[A-Z0-9-]+$/)
          .nullable(),
        executableBinding: z.string().min(1).nullable(),
      }),
    )
    .length(PROVIDER_COMPLETENESS_CASES.length),
});

export type ExperienceCapability = z.infer<typeof ExperienceCapabilitySchema>;

export const SharedSemanticProofSchema = z.object({
  id: z.string().regex(/^EXP-SHARED-[A-Z0-9-]+$/),
  purpose: z.string().min(1),
  subjectAndCohort: z.string().min(1),
  fieldMeanings: z.string().min(1),
  statuses: z.string().min(1),
  actorAuthority: z.string().min(1),
  actions: z.string().min(1),
  completionEvidence: z.string().min(1),
  vocabulary: z.string().min(1),
  notifications: z.string().min(1),
  help: z.string().min(1),
  exports: z.string().min(1),
  fallback: z.string().min(1),
  regulatedMeaning: z.string().min(1),
  owner: z.string().min(1),
  reviewedAt: z.string().date(),
});

export type SharedSemanticProof = z.infer<typeof SharedSemanticProofSchema>;

export function canPresentDestination(
  destination: Pick<ExperienceDestination, 'firewallFamilies' | 'resolutionStates'>,
  input: {
    firewallFamily: (typeof FIREWALL_FAMILIES)[number];
    resolutionStatus: (typeof RESOLUTION_STATUSES)[number];
  },
): boolean {
  if (input.firewallFamily === 'UNKNOWN' || input.resolutionStatus === 'CONFLICT') return false;
  return (
    destination.firewallFamilies.includes(input.firewallFamily) &&
    destination.resolutionStates.includes(input.resolutionStatus)
  );
}
